import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Facebook, Instagram, Linkedin, BookOpen } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function Footer() {
  const year = new Date().getFullYear();
  const settings = useSiteSettings();

  const SOCIALS = [
    { id: "facebook", icon: Facebook, label: "Facebook", href: settings.facebook },
    { id: "instagram", icon: Instagram, label: "Instagram", href: settings.instagram },
    { id: "linkedin", icon: Linkedin, label: "LinkedIn", href: settings.linkedin },
  ].filter((s) => s.href && s.href.trim() !== "");

  return (
    <footer data-testid="site-footer" className="bg-[#0B1E36] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-12">
          {/* Logo + description + socials */}
          <div>
            <a href="/#inicio" data-testid="footer-logo" className="flex items-center select-none">
              <img src="/assets/logo-cropped.png" alt="Solution Car Premium" className="h-16 w-auto" />
            </a>
            <p className="mt-6 text-sm leading-relaxed text-slate-300 max-w-sm">
              Comercialização de automóveis, importação de viaturas da Europa para Portugal e apoio
              ao financiamento automóvel — com confiança, transparência e segurança.
            </p>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ id, icon: Icon, label, href }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${id}`}
                  className="w-10 h-10 flex items-center justify-center border border-white/20 text-slate-300 hover:bg-white hover:text-[#0072C6] hover:border-white transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 mb-6">Serviços</h3>
            <ul className="space-y-3 text-sm text-slate-300" data-testid="footer-services">
              <li><a href="/#stock" className="hover:text-white transition-colors" data-testid="footer-link-stock">Stock de Viaturas</a></li>
              <li><a href="/#importacao" className="hover:text-white transition-colors" data-testid="footer-link-import">Importação da Europa</a></li>
              <li><a href="/#financiamento" className="hover:text-white transition-colors" data-testid="footer-link-financing">Financiamento Automóvel</a></li>
              <li><a href="/#simulador" className="hover:text-white transition-colors" data-testid="footer-link-simulator">Simulador de Financiamento</a></li>
              <li><a href="/#contacto" className="hover:text-white transition-colors" data-testid="footer-link-quote">Pedido de Cotação</a></li>
              <li><a href="/seja-vendedor" className="hover:text-white transition-colors" data-testid="footer-link-seller">Seja Nosso Vendedor</a></li>
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 mb-6">Navegação</h3>
            <ul className="space-y-3 text-sm text-slate-300" data-testid="footer-nav">
              <li><a href="/#destaques" className="hover:text-white transition-colors" data-testid="footer-link-featured">Viaturas em Destaque</a></li>
              <li><a href="/#novidades" className="hover:text-white transition-colors" data-testid="footer-link-latest">Últimas Viaturas</a></li>
              <li><a href="/#testemunhos" className="hover:text-white transition-colors" data-testid="footer-link-testimonials">Testemunhos</a></li>
            </ul>
          </div>

          {/* Informações Legais */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 mb-6">Informações Legais</h3>
            <ul className="space-y-3 text-sm text-slate-300" data-testid="footer-legal">
              <li>
                <a href="https://www.livroreclamacoes.pt/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-2" data-testid="footer-link-complaints">
                  <BookOpen className="w-4 h-4 text-slate-400 shrink-0" /> Livro de Reclamações Eletrónico
                </a>
              </li>
              <li><Link to="/quem-somos" className="hover:text-white transition-colors" data-testid="footer-link-about-page">Quem Somos</Link></li>
              <li><Link to="/politica-de-privacidade" className="hover:text-white transition-colors" data-testid="footer-link-privacy">Política de Privacidade</Link></li>
              <li><Link to="/protecao-de-dados" className="hover:text-white transition-colors" data-testid="footer-link-data">Proteção de Dados</Link></li>
              <li><Link to="/politica-de-cookies" className="hover:text-white transition-colors" data-testid="footer-link-cookies">Política de Cookies</Link></li>
              <li><Link to="/termos-e-condicoes" className="hover:text-white transition-colors" data-testid="footer-link-terms">Termos e Condições</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400 mb-6">Contacto</h3>
            <ul className="space-y-4 text-sm text-slate-300" data-testid="footer-contact">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{settings.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-slate-400 shrink-0" />
                <span>WhatsApp disponível</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{settings.email}</span>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                <span>{settings.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <p data-testid="footer-copyright">© {year} SOLUTION CAR PREMIUM. Todos os direitos reservados.</p>
          <p>Comercialização · Importação · Financiamento</p>
        </div>
      </div>
    </footer>
  );
}
